import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import LocalStorageKey from 'config/localStorageKey';
import {
  getInformationList,
  newMessageDraft,
  setInformationList,
  useChatCurrentGptModel,
  useIsChatEmpty,
  useStoredActiveConversationId,
  useStoredBrandVoiceId,
  useStoredTargetAudienceId,
} from 'features/aiWriter/AiWriterSidebar/steps/chat/chatStore';
import { useMessageStreamingIndicatorStore } from 'features/aiWriter/AiWriterSidebar/steps/chat/messageStreamingIndicatorStore';
import { useResetConversationMutation } from 'features/aiWriter/AiWriterSidebar/steps/chat/useResetConversationMutation';
import { useWarnAboutChatReset } from 'features/aiWriter/AiWriterSidebar/steps/chat/useShowChatResetModal';
import {
  ChatOptimizePromptButton,
  IMPROVE_PROMPT_OUTPUT_TYPE,
} from 'features/aiWriter/chat/ChatOptimizePromptButton';
import { ChatSendButton } from 'features/aiWriter/chat/ChatSendButton';
import { ChatSettingsPopover } from 'features/aiWriter/chat/ChatSettingsPopover';
import { GenericMessageInput } from 'features/aiWriter/chat/GenericMessageInput';
import { useIsGpt4Disabled } from 'features/aiWriter/chat/useIsGpt4Disabled';
import { useShowTemplatesModal } from 'features/aiWriter/commandTemplates/useShowTemplatesModal';
import { useUpdateCommandTemplateUsage } from 'features/aiWriter/commandTemplates/useUpdateCommandTemplateUsage';
import { useGetProjectOrPreselectedLanguageAndCountry } from 'features/aiWriter/hooks/useGetProjectOrPreselectedLanguageAndCountry';
import { ProjectBrandVoiceSelector } from 'features/aiWriter/ProjectBrandVoiceSelector';
import { ProjectTargetAudienceSelector } from 'features/aiWriter/ProjectTargetAudienceSelector';
import { getActiveTab } from 'features/aiWriter/store/selectors';
import { useShowApplyInformationModal } from 'features/information/apply-information/ApplyInformationModal';
import { InformationButton } from 'features/information/apply-information/InformationButton';
import { usePromptOptimizerControlled } from 'features/textGenerator/promptOptimizer/usePromptOptimizerContorlled';
import { trackingWrapper } from 'features/tracking/wrapper/TrackingWrapper';
import { usePostHog } from 'posthog-js/react';
import { KeyboardEvent, useState } from 'react';
import {
  GPT_MODELS,
  GptModel,
} from 'services/backofficeIntegration/http/endpoints/aiWriter/httpCreateConversation';
import { httpStopStreamMessage } from 'services/backofficeIntegration/http/endpoints/aiWriter/httpStopStreamMessage';
import { ChatMessageSource, GAEvents } from 'services/tracking/GAEvents';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

export type ChatMessageInput = {
  isSending: boolean;
  value: string;
  sendButtonConfig: { gtmId: string; disabled?: boolean };
  onChange: (message: string) => void;
  onSend: () => void;
};

export function ChatMessageInput({ isSending, value, onChange, onSend }: ChatMessageInput) {
  const activeConversationId = useStoredActiveConversationId();
  const postHog = usePostHog();
  const { language, country } = useGetProjectOrPreselectedLanguageAndCountry();

  const isMessageBeingStreamed = useMessageStreamingIndicatorStore(
    state => state.isMessageBeingStreamed
  );
  const setIsMessageBeingStreamed = useMessageStreamingIndicatorStore(
    state => state.setIsMessageBeingStreamed
  );

  const { mutateAsync: stopStreamMessage, isLoading: isStopStreamMessageLoading } = useMutation({
    mutationFn: httpStopStreamMessage.callEndpoint,
  });

  const {
    isUndoVisible,
    setInitialPrompt,
    optimizePromptMutation,
    undoPromptOptimization,
    setIsUndoVisible,
  } = usePromptOptimizerControlled({
    prompt: value,
    setPrompt: (prompt: string) => onChange(prompt),
    locale: { language, country },
    initialPrompt: '',
    outputType: IMPROVE_PROMPT_OUTPUT_TYPE,
    callback: () => trackingWrapper.track('promptOptimized', { prompt: value }),
  });
  const { mutate: optimizePrompt, isLoading: isOptimizationLoading } = optimizePromptMutation;

  const isDisabled =
    isSending || isMessageBeingStreamed || isStopStreamMessageLoading || isOptimizationLoading;

  function sendMessage({ source }: { source: ChatMessageSource }) {
    GAEvents.sentChatMessage({ source });
    postHog?.capture('CF - chat message sent via chat');
    onSend();
  }

  const handleStopStreamMessage = async () => {
    await stopStreamMessage({ conversationId: activeConversationId });
    setIsMessageBeingStreamed(false);
    GAEvents.stopStreamingClicked();
  };

  const handleTextFieldChange = (text: string) => {
    onChange(text);
    if (isUndoVisible) {
      setIsUndoVisible(false);
    }
  };

  const handleOptimizeClick = () => {
    setInitialPrompt(value);
    optimizePrompt();
  };

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!e.shiftKey && e.key === 'Enter') {
      e.preventDefault();

      if (isDisabled) {
        return;
      }

      // There is a small delay after
      // - user sends a message
      // - the message is being streamed
      // - the streaming is done
      // - we're fetching the list of messages
      // In this case we don't want to send a new message because
      // it throws away the previous one and acts like a reset.
      // In this case we still have the optimistic question but
      // less than 2 stored messages.

      // Update: After playing around with this even more, it turns out
      //         the API response of /messages is empty in some cases.
      //         This leads to stored messages being empty (or less than 2)
      //         and the optimistic question still being present.
      //         This is a bug in API and must be solved first.
      //         For now, I've to disable this check to prevent not being able
      //         to send new messages in this case.
      /*
        if (optimisticQuestion && storedMessages.length < 2) {
          return;
        }
         */

      sendMessage({ source: 'enter' });
    }
  }

  return (
    <GenericMessageInput
      value={value}
      onChange={handleTextFieldChange}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      onSend={() => sendMessage({ source: 'enter' })}
      testId="chat-message-input"
    >
      <LeftContainer>
        <ChatSettings />
        <BrandVoiceSelect />
        <AudienceSelect />
        <InformationSelect />
      </LeftContainer>

      <Stack direction="row">
        <ChatOptimizePromptButton
          prompt={value}
          undoVisible={isUndoVisible}
          loading={isOptimizationLoading}
          onOptimizeClick={handleOptimizeClick}
          onUndoClick={undoPromptOptimization}
        />
        <ChatSendButton
          {...withGtmInteraction('GTM_aiWriter_projectOverview_chat_sendMessage')}
          isSending={isSending || isMessageBeingStreamed}
          onSend={() => sendMessage({ source: 'button' })}
          disabled={isDisabled}
          onStop={handleStopStreamMessage}
        />
      </Stack>
    </GenericMessageInput>
  );
}

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings.one};
`;

const InformationSelect = () => {
  const showApplyInformationModal = useShowApplyInformationModal();
  const informationList = getInformationList();

  const handleInformationChange = () => {
    showApplyInformationModal({
      preselectedInformation: informationList,
      applyGtmId: gtmIds.aiWriter.chat.applyInformation,
      onApply: informationList => {
        setInformationList(informationList);
      },
    });
  };

  return <InformationButton onInformationChange={handleInformationChange} />;
};

const BrandVoiceSelect = () => {
  return <ProjectBrandVoiceSelector />;
};

const AudienceSelect = () => {
  return <ProjectTargetAudienceSelector />;
};

const ChatSettings = () => {
  const currentGptModel = useChatCurrentGptModel();
  const embeddingModelId = useAppSelector(getActiveTab).embeddingModelId;
  const isGpt4Disabled = useIsGpt4Disabled();
  const isChatEmpty = useIsChatEmpty();
  const activeConversationId = useStoredActiveConversationId();
  const brandVoiceId = useStoredBrandVoiceId();
  const targetAudienceId = useStoredTargetAudienceId();
  const warnAboutChatReset = useWarnAboutChatReset();
  const showTemplatesModal = useShowTemplatesModal();
  const { mutateAsync: resetConversation } = useResetConversationMutation();
  const { mutate: updateCommandTemplateUsage } = useUpdateCommandTemplateUsage();

  const [storedGptModel, setStoredGptModel] = useLocalStorage<GptModel>(
    LocalStorageKey.DefaultGptModel,
    GPT_MODELS.GPT_3_5,
    false
  );
  const initGptModel = currentGptModel ?? storedGptModel ?? GPT_MODELS.GPT_3_5;
  const [gptModel, setGptModel] = useState<GptModel>(initGptModel);

  const handleGptModelChange = (gptModel: GptModel) => {
    if (activeConversationId && !isChatEmpty) {
      warnAboutChatReset({
        onAccept: () => {
          setGptModel(gptModel);
          setStoredGptModel(gptModel);
          resetConversation({ brandVoiceId, gptModel, targetAudienceId });
        },
      });
    } else {
      setGptModel(gptModel);
      setStoredGptModel(gptModel);
      resetConversation({ brandVoiceId, gptModel, targetAudienceId });
    }
  };

  const handleSelectTemplate = (template: string) => {
    newMessageDraft({ text: template, sendInstantly: true });
  };

  const handleResetChat = () => {
    resetConversation({ brandVoiceId, gptModel, targetAudienceId });
  };

  const isMessageBeingStreamed = useMessageStreamingIndicatorStore(
    state => state.isMessageBeingStreamed
  );
  const isResetChatDisabled = isChatEmpty || isMessageBeingStreamed;

  return (
    <ChatSettingsPopover
      gptModel={gptModel}
      onGptModelChange={handleGptModelChange}
      isGpt4Disabled={isGpt4Disabled}
      modelId={embeddingModelId}
      onTemplateSelection={handleSelectTemplate}
      onResetChat={handleResetChat}
      isResetChatDisabled={isResetChatDisabled}
      menuOpeningDirection="top"
    />
  );
};
